import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import { useLanguageStore } from '../../stores/languageStore';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Pagination from '../../components/ui/Pagination';
import ViewToggle, { ViewMode } from '../../components/ui/ViewToggle';
import axios from 'axios';

interface Lawsuit {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  cost: number;
  currency?: string;
  category: string;
  isPremiumAnalysis?: boolean;
  aiAnalysis?: {
    totalCost?: number;
    currency?: string;
    estimatedClaimAmount?: {
      amount?: number;
      currency?: string;
    };
  };
}

interface PaginatedLawsuitsResponse {
  data: Lawsuit[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { isRTL } = useLanguageStore();

  // State for pagination and view mode
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Fetch user's lawsuits with pagination
  const { data: lawsuitsResponse, isLoading: lawsuitsLoading } = useQuery<PaginatedLawsuitsResponse>({
    queryKey: ['my-lawsuits', currentPage, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });
      const response = await axios.get(`/api/lawsuits/my?${params.toString()}`);
      
      // Transform response to match our interface  
      const responseData = response.data;
      if (responseData.success && responseData.pagination) {
        return {
          data: responseData.data,
          pagination: {
            page: responseData.pagination.page,
            pageSize: responseData.pagination.limit,
            total: responseData.pagination.total,
            totalPages: responseData.pagination.pages
          }
        };
      }
      
      // Fallback for unexpected response structure
      return {
        data: responseData.data || [],
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          total: 0,
          totalPages: 1
        }
      };
    },
    enabled: !!user,
  });

  const myLawsuits = lawsuitsResponse?.data || [];
  const pagination = lawsuitsResponse?.pagination;

  React.useEffect(() => {
    console.log('myLawsuits:', myLawsuits);
    if (!lawsuitsLoading && myLawsuits && myLawsuits.length === 0 && currentPage === 1) {
      navigate('/file-lawsuit', { replace: true });
    }
  }, [lawsuitsLoading, myLawsuits, navigate, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'won':
        return 'bg-green-100 text-green-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      case 'settled':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'submitted':
        return 'bg-purple-100 text-purple-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper to map lawsuit status to wizard step
  const getWizardStepForStatus = (status: string): string | null => {
    switch (status) {
      case 'draft':
        return 'caseDetails';
      case 'analyzing':
        return 'aiAnalysis';
      case 'questions_ready':
        return 'aiAnalysis';
      case 'analysis_complete':
        return 'verification';
      default:
        return null;
    }
  };

  const formatDisplayAmount = (lawsuit: Lawsuit) => {
    // Priority: 1. AI analysis estimated claim amount, 2. AI analysis total cost, 3. Default cost
    const estimatedAmount = lawsuit.aiAnalysis?.estimatedClaimAmount?.amount;
    const totalCost = lawsuit.aiAnalysis?.totalCost;
    const defaultCost = lawsuit.cost;
    
    let displayAmount = 0;
    let displayCurrency = '₪';
    
    if (estimatedAmount && estimatedAmount > 0) {
      displayAmount = estimatedAmount;
      displayCurrency = lawsuit.aiAnalysis?.estimatedClaimAmount?.currency || lawsuit.aiAnalysis?.currency || lawsuit.currency || '₪';
    } else if (totalCost && totalCost > 0) {
      displayAmount = totalCost;
      displayCurrency = lawsuit.aiAnalysis?.currency || lawsuit.currency || '₪';
    } else if (defaultCost && defaultCost > 0) {
      // Convert from cents to dollars if it's the default cost (9900 cents = $99)
      displayAmount = defaultCost >= 1000 ? defaultCost / 100 : defaultCost;
      displayCurrency = '$';
    }
    
    return `${displayCurrency} ${displayAmount.toLocaleString()}`;
  };

  const handleLawsuitClick = (lawsuit: Lawsuit) => {
    const step = getWizardStepForStatus(lawsuit.status);
    if (step) {
      // Open the wizard at the relevant step
      navigate(`/file-lawsuit?lawsuitId=${lawsuit.id}&step=${step}`);
    } else {
      // Completed lawsuits: go to detail page
      navigate(`/lawsuit/${lawsuit.id}`);
    }
  };

  const renderGridView = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {myLawsuits.filter(lawsuit => lawsuit.id && lawsuit.title).map((lawsuit) => (
        <div
          key={lawsuit.id}
          className="relative border border-gray-200 rounded-xl p-5 bg-white hover:shadow-lg hover:border-blue-400 cursor-pointer transition-all duration-200 shadow-sm flex flex-col min-h-[150px]"
          onClick={() => handleLawsuitClick(lawsuit)}
        >
          {/* Title */}
          <h4 className="text-lg font-bold text-black mb-2 mt-2 truncate pr-2" style={{lineHeight: '1.3'}}>{lawsuit.title}</h4>
          {/* Category and date */}
          <div className="text-xs text-gray-500 mb-4 flex items-center gap-2">
            <span>
              {(t(`categories.${lawsuit.category}`) !== `categories.${lawsuit.category}` && lawsuit.category !== undefined)
                ? t(`categories.${lawsuit.category}`)
                : (lawsuit.category || '')}
            </span>
            <span>•</span>
            <span>{new Date(lawsuit.createdAt).toLocaleDateString()}</span>
          </div>
          {/* Cost */}
          <div className="mt-auto text-xl font-semibold text-blue-700">
            {formatDisplayAmount(lawsuit)}
          </div>
          {/* Premium badge - elegant ribbon stamp */}
          {lawsuit.isPremiumAnalysis && (
            <div className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} z-10`}>
              <div className="relative">
                {/* Ribbon background with shadow */}
                <div className={`bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400 text-white text-xs font-bold px-3 py-1.5 rounded-md shadow-lg transform ${isRTL ? '-rotate-3' : 'rotate-3'} hover:rotate-0 transition-transform duration-200`}>
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-200">✨</span>
                    <span>{t('lawsuit.premium', 'Premium')}</span>
                    <span className="text-yellow-200">✨</span>
                  </div>
                </div>
                {/* Ribbon tail effect */}
                <div className={`absolute top-full ${isRTL ? 'right-0' : 'left-0'} w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-amber-400 transform ${isRTL ? '-rotate-3' : 'rotate-3'}`}></div>
              </div>
            </div>
          )}
          
          {/* Status badge - bottom right for LTR, bottom left for RTL */}
          <span className={`absolute bottom-4 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(lawsuit.status)} shadow-sm ${isRTL ? 'left-4' : 'right-4'}`}
                style={{minWidth: '90px', textAlign: 'center'}}>
            {t(`lawsuit.statuses.${lawsuit.status}`) !== `lawsuit.statuses.${lawsuit.status}`
              ? t(`lawsuit.statuses.${lawsuit.status}`)
              : lawsuit.status}
          </span>
        </div>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="space-y-4">
      {myLawsuits.filter(lawsuit => lawsuit.id && lawsuit.title).map((lawsuit) => (
        <div
          key={lawsuit.id}
          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-blue-400 cursor-pointer transition-all duration-200"
          onClick={() => handleLawsuitClick(lawsuit)}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-1">
                    <h4 className="text-lg font-bold text-black truncate">{lawsuit.title}</h4>
                    {lawsuit.isPremiumAnalysis && (
                      <div className="flex items-center gap-1 bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400 text-white text-xs font-bold px-2 py-1 rounded-md w-fit mt-1 sm:mt-0">
                        <span className="text-yellow-200">✨</span>
                        <span>{t('lawsuit.premium', 'Premium')}</span>
                        <span className="text-yellow-200">✨</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm text-gray-500 space-y-1 sm:space-y-0">
                    <span>
                      {(t(`categories.${lawsuit.category}`) !== `categories.${lawsuit.category}` && lawsuit.category !== undefined)
                        ? t(`categories.${lawsuit.category}`)
                        : (lawsuit.category || '')}
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span>{new Date(lawsuit.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between sm:justify-end sm:space-x-4 sm:ml-4">
              <div className="text-left sm:text-right">
                <div className="text-xl font-semibold text-blue-700">
                  {formatDisplayAmount(lawsuit)}
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(lawsuit.status)} whitespace-nowrap`}>
                {t(`lawsuit.statuses.${lawsuit.status}`) !== `lawsuit.statuses.${lawsuit.status}`
                  ? t(`lawsuit.statuses.${lawsuit.status}`)
                  : lawsuit.status}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (lawsuitsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('dashboard.welcome', { name: user?.name || user?.email })}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Lawsuits Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                {t('dashboard.myLawsuits', 'My Lawsuits')}
                {pagination && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({pagination.total} {t('dashboard.total', 'total')})
                  </span>
                )}
              </h3>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-4 sm:gap-0">
                <ViewToggle 
                  currentView={viewMode} 
                  onViewChange={setViewMode}
                  className="w-full sm:w-auto"
                />
                <button
                  onClick={() => navigate('/file-lawsuit')}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  <span className="truncate">
                    {myLawsuits && myLawsuits.length > 0
                      ? t('dashboard.fileLawsuit', 'File Lawsuit')
                      : t('dashboard.createFirst', 'Create New Lawsuit')}
                  </span>
                </button>
              </div>
            </div>

            {myLawsuits && myLawsuits.length > 0 ? (
              <>
                {viewMode === 'grid' ? renderGridView() : renderListView()}
                
                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.total}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={[6, 12, 24, 48]}
                  />
                )}
              </>
            ) : (
              <div className="text-center text-gray-500 py-8">
                {t('dashboard.noLawsuits', 'No lawsuits found.')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage; 